import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Breadcrumb,
    Row,
    Col,
    Card,
    notification,
    Button,
    Spin,
    Image,
    Table,
    Space,
    Popconfirm,
    Tag,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import http from "../../helpers/http";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/";
const base_url = `${API_URL}admin/journals`;
const image_url = `${API_URL}uploads/images/`;

function BlogList() {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchPosts = async () => {
        setLoading(true);
        try {
            const res = await http.get(base_url);
            const data = res?.data || res;
            if (data?.posts) {
                setPosts(data.posts);
            } else if (Array.isArray(data)) {
                setPosts(data);
            } else {
                setPosts([]);
            }
        } catch (error) {
            notification.error({
                message: "Error fetching blogs",
                description: error?.message,
            });
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await http.delete(`${base_url}/${id}`);
            notification.success({ message: "Blog deleted successfully" });
            fetchPosts();
        } catch (error) {
            notification.error({
                message: "Error deleting blog",
                description: error?.message,
            });
        }
    };

    const columns = [
        {
            title: "Image",
            dataIndex: "img",
            key: "img",
            width: 80,
            render: (img) =>
                img ? (
                    <Image
                        width={50}
                        height={50}
                        src={image_url + img}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    <div
                        style={{
                            width: 50,
                            height: 50,
                            background: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            borderRadius: 4,
                        }}
                    >
                        No Image
                    </div>
                ),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            ellipsis: true,
            sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
        },
        {
            title: "Subtitle",
            dataIndex: "subTitle",
            key: "subTitle",
            width: 120,
            render: (subTitle) => (subTitle ? <Tag>{subTitle}</Tag> : "—"),
        },
        {
            title: "Tag",
            dataIndex: "tag",
            key: "tag",
            render: (tag) => (tag ? <Tag color="blue">{tag}</Tag> : "—"),
        },
        {
            title: "Tags",
            dataIndex: "tags",
            key: "tags",
            render: (tags) => (
                <Space size={[4, 4]} wrap>
                    {tags?.length > 0
                        ? tags.map((t, idx) => (
                            <Tag key={idx} color="default">{t}</Tag>
                        ))
                        : "—"}
                </Space>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag
                    color={
                        status === "active"
                            ? "green"
                            : status === "inactive"
                                ? "red"
                                : "orange"
                    }
                >
                    {status?.toUpperCase()}
                </Tag>
            ),
        },
        // {
        //     title: "Views",
        //     dataIndex: "viewCount",
        //     key: "viewCount",
        //     width: 80,
        //     sorter: (a, b) => (a.viewCount || 0) - (b.viewCount || 0),
        // },
        {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => navigate(`/blogs/edit/${record._id}`)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this blog post?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        fetchPosts();
    }, []);

    return (
        <div>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>JagKris</Breadcrumb.Item>
                <Breadcrumb.Item>Blogs</Breadcrumb.Item>
            </Breadcrumb>

            <Row>
                <Col span={24}>
                    <Card
                        title="Blogs"
                        extra={
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate("/blogs/create")}
                            >
                                Add New Blog
                            </Button>
                        }
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: 50 }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={posts}
                                rowKey="_id"
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 1000 }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default BlogList;
